import { authApi } from '@oneview/api/authApi';
import { settingApi } from '@oneview/api/settingApi';
import { DetailsTabId } from '@oneview/constants/ids';
import { reorder } from '@/utils/miscUtils';
import { createReducer } from '@reduxjs/toolkit';
import { cloneDeep, filter, findIndex } from 'lodash';
import { PersistConfig, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';
import { advisorMenu } from '../../constants/menus';
import { detailsTab, Tab, TabDetails, TabId } from '../../constants/tabs';
import {
    changeTab,
    closeAllRightTabs,
    closeAllSiblingTabsAfter,
    closeAllSiblingTabsExcept,
    closeAllTabs,
    closeAllTabsExcept,
    closeSiblingTab,
    closeTab,
    dragSiblingTab,
    dragTab,
    openTab,
    popinTab,
    popoutTab,
    replaceCurrentTab,
    setActiveSiblingTabIndex,
    updateTab,
} from './tabBarActions';

const DEFAULT_STATE: {
    tabs: TabDetails[];
    activeTabId: TabId;
    activeTabIndex: number;
} = {
    tabs: [
        {
            ...(advisorMenu(true)?.[0] as Tab),
            poppedOut: false,
            closeable: false,
            draggable: false,
            activeSiblingTabIndex: 0,
            siblingTabs: [advisorMenu(true)?.[0] as Tab],
        },
    ],
    activeTabId: advisorMenu(true)?.[0]?.id as TabId,
    activeTabIndex: 0,
};

const tabBarReducer = createReducer(DEFAULT_STATE, (builder) => {
    builder?.addCase(openTab, (state, { payload }) => {
        const { isGroupUpTabsEnabled, tab: newTab } = payload;
        const detailsTabName = newTab.id in detailsTab ? detailsTab[newTab.id as DetailsTabId].name : newTab.name;
        if (isGroupUpTabsEnabled) {
            // Scenario when Group Up tabs is enabled in Platform Setting
            const newTabIndex = findIndex(state.tabs, (tab) => tab.id === newTab.id);
            const hasSiblingTabs = newTabIndex !== -1;
            if (!hasSiblingTabs) {
                const tab: TabDetails = {
                    id: newTab.id,
                    name: detailsTabName,
                    activeSiblingTabIndex: 0,
                    draggable: true,
                    closeable: true,
                    poppedOut: false,
                    siblingTabs: [newTab],
                    init: newTab.init,
                };
                state.tabs.push(tab);
                state.activeTabId = newTab.id;
                state.activeTabIndex = state.tabs.length - 1;
            } else {
                const isTabAlreadyOpened = state.tabs[newTabIndex]!.siblingTabs.some(
                    (siblingTab) => siblingTab.route === newTab.route,
                );
                if (isTabAlreadyOpened) {
                    const existingSiblingTabIndex = findIndex(
                        state.tabs[newTabIndex]!.siblingTabs,
                        (tab) => tab.route === newTab.route,
                    );
                    state.tabs[newTabIndex]!.activeSiblingTabIndex = existingSiblingTabIndex;
                } else {
                    state.activeTabId = newTab.id;
                    state.tabs[newTabIndex]!.siblingTabs.push(newTab);
                }

                state.activeTabIndex = newTabIndex;
            }
        } else {
            // Scenario when Group Up tabs is disabled in Platform Setting
            const newTabIndex = findIndex(
                state.tabs,
                (tab) => tab.id === newTab.id && tab.siblingTabs[0]?.route === newTab.route,
            );
            const isTabAlreadyExists = newTabIndex !== -1;
            const tab = {
                id: newTab.id,
                name: detailsTabName,
                activeSiblingTabIndex: 0,
                draggable: true,
                closeable: true,
                poppedOut: false,
                siblingTabs: [newTab],
                init: newTab.init,
            };
            if (isTabAlreadyExists) {
                state.activeTabId = newTab.id;
                state.activeTabIndex = newTabIndex;
            } else {
                state.tabs.push(tab);
                state.activeTabId = newTab.id;
                state.activeTabIndex = state.tabs.length - 1;
            }
        }
    });

    builder.addCase(changeTab, (state, { payload }) => {
        state.activeTabIndex = payload;
        state.activeTabId = state.tabs[payload]!.id;
    });

    builder.addCase(replaceCurrentTab, (state, { payload }) => {
        state.activeTabId = payload.id;
        state.tabs[state.activeTabIndex]!.siblingTabs[state.tabs[state.activeTabIndex]!.activeSiblingTabIndex] =
            payload;
    });

    builder.addCase(setActiveSiblingTabIndex.fulfilled, (state, { payload: { tabIndex, siblingTabIndex } }) => {
        state.activeTabIndex = tabIndex;
        state.activeTabId = state.tabs[tabIndex]!.id;
        state.tabs[tabIndex]!.activeSiblingTabIndex = siblingTabIndex;
    });

    builder.addCase(closeAllSiblingTabsExcept.fulfilled, (state, { payload: { tabIndex, siblingTabIndex } }) => {
        state.tabs[tabIndex]!.activeSiblingTabIndex = 0;
        state.tabs[tabIndex]!.siblingTabs = filter(
            state.tabs[tabIndex]!.siblingTabs,
            (_, index) => index === siblingTabIndex,
        );
    });

    builder.addCase(closeAllSiblingTabsAfter.fulfilled, (state, { payload: { tabIndex, siblingTabIndex } }) => {
        const currentActiveTabIndex = state.tabs[tabIndex]!.activeSiblingTabIndex;
        state.tabs[tabIndex]!.siblingTabs = filter(
            state.tabs[tabIndex]!.siblingTabs,
            (_, index) => index <= siblingTabIndex,
        );
        state.tabs[tabIndex]!.activeSiblingTabIndex =
            currentActiveTabIndex <= siblingTabIndex
                ? currentActiveTabIndex
                : state.tabs[tabIndex]!.siblingTabs.length - 1;
    });

    builder.addCase(
        closeSiblingTab.fulfilled,
        (state, { payload: { tabIndex, siblingTabIndex, newActiveTabIndexRoute } }) => {
            state.tabs[tabIndex]!.siblingTabs = filter(
                state.tabs[tabIndex]!.siblingTabs,
                (_, index) => index !== siblingTabIndex,
            );
            state.tabs[tabIndex]!.activeSiblingTabIndex = findIndex(
                state.tabs[tabIndex]!.siblingTabs,
                (tab) => tab.route === newActiveTabIndexRoute,
            );
        },
    );

    builder.addCase(updateTab, (state, { payload }) => {
        const activeTab = state.tabs[state.activeTabIndex]!;
        const tabIndex = findIndex(
            activeTab.siblingTabs,
            (siblingTab) => activeTab.id === payload.id && siblingTab.route === payload.route,
        );

        if (tabIndex >= 0) {
            state.tabs[state.activeTabIndex]!.siblingTabs[tabIndex] = {
                ...state.tabs[state.activeTabIndex]!.siblingTabs[tabIndex],
                ...payload,
            };
        }
    });

    builder.addCase(dragTab, (state, { payload }) => {
        const { tabs, activeTabIndex } = state;
        const activeTab = tabs[activeTabIndex]!;
        state.tabs = reorder(state.tabs, payload.sourceIndex, Math.max(1, payload.destinationIndex));
        state.activeTabIndex = state.tabs.indexOf(activeTab);
    });

    builder.addCase(dragSiblingTab, (state, { payload: { tabIndex, destinationIndex, sourceIndex } }) => {
        const { tabs, activeTabIndex } = state;
        const activeTab = tabs[activeTabIndex]!;
        const { siblingTabs, activeSiblingTabIndex } = activeTab;
        const activeSiblingTab = siblingTabs[activeSiblingTabIndex]!;
        state.tabs[tabIndex]!.siblingTabs = reorder(
            state.tabs[tabIndex]!.siblingTabs,
            sourceIndex,
            Math.max(1, destinationIndex),
        );
        state.tabs[tabIndex]!.activeSiblingTabIndex = state.tabs[tabIndex]!.siblingTabs.indexOf(activeSiblingTab);
    });

    builder.addCase(popoutTab, (state, { payload: tabIndex }) => {
        state.tabs[tabIndex]!.poppedOut = true;
    });

    builder.addCase(popinTab, (state, { payload: tabIndex }) => {
        state.tabs[tabIndex]!.poppedOut = false;
    });

    builder.addCase(closeTab, (state, { payload: tabIndex }) => {
        state.tabs.splice(tabIndex, 1);
        state.activeTabIndex = Math.min(
            state.tabs.length - 1,
            Math.max(0, tabIndex < state.activeTabIndex ? state.activeTabIndex - 1 : state.activeTabIndex),
        );
        state.activeTabId = state.tabs[state.activeTabIndex]!.id;
    });

    builder.addCase(closeAllTabs.fulfilled, (state) => {
        state.tabs.splice(1, state.tabs.length);
    });

    builder.addCase(closeAllRightTabs.fulfilled, (state, { payload: tabIndex }) => {
        state.tabs.splice(tabIndex + 1, state.tabs.length);
    });

    builder.addCase(closeAllTabsExcept.fulfilled, (state, { payload: tabIndex }) => {
        state.tabs = state.tabs.filter((tab, index) => !tab.closeable || index === tabIndex);
    });

    builder.addMatcher(settingApi.endpoints.restoreSettings.matchFulfilled, (state, { payload }) => {
        const savedPoppedOutTabs = payload.advisor?.poppedOutTabs?.savedTabs;
        if (savedPoppedOutTabs && savedPoppedOutTabs.length > 0) {
            (savedPoppedOutTabs as Tab[]).forEach((poppedOutTab) => {
                const newTabIndex = findIndex(
                    state.tabs,
                    (tab) =>
                        tab.id === poppedOutTab.id &&
                        tab.siblingTabs[tab.activeSiblingTabIndex]?.route === poppedOutTab.route,
                );
                const isTabAlreadyOpened = newTabIndex !== -1;
                const tab = {
                    ...poppedOutTab,
                };

                if (!isTabAlreadyOpened) {
                    state.tabs[state.activeTabIndex]?.siblingTabs.push(tab);
                    state.tabs[state.activeTabIndex] = {
                        ...state.tabs[state.activeTabIndex]!,
                        poppedOut: state.tabs[state.activeTabIndex]?.poppedOut ?? true,
                        closeable: state.tabs[state.activeTabIndex]?.closeable ?? true,
                        draggable: state.tabs[state.activeTabIndex]?.draggable ?? true,
                    };
                    state.activeTabId = poppedOutTab.id;
                    state.activeTabIndex = state.tabs.length - 1;
                } else {
                    state.activeTabId = poppedOutTab.id;
                }
            });
        }
    });

    builder.addMatcher(authApi.endpoints.logout.matchFulfilled, () => DEFAULT_STATE);
    builder.addMatcher(settingApi.endpoints.saveSettings.matchFulfilled, (state, { payload }) => {
        const { groupUpTabs } = payload.common?.global || {};

        if (groupUpTabs) {
            const newTabsState = Object.values(
                state.tabs.reduce<Record<TabId, TabDetails>>((result, item) => {
                    const tab = result[item.id];
                    if (tab) {
                        tab.siblingTabs.push(...item.siblingTabs);
                    } else {
                        result[item.id] = cloneDeep(item);
                    }
                    return result;
                }, {}),
            );

            state.tabs = newTabsState;
            state.activeTabIndex = findIndex(newTabsState, (item) => item.id === state.activeTabId);
        } else {
            const newTabsState = state.tabs.flatMap((tab) =>
                tab.siblingTabs.map((siblingTab) => ({
                    ...tab,
                    activeSiblingTabIndex: 0,
                    siblingTabs: [siblingTab],
                })),
            );

            state.tabs = newTabsState;
            state.activeTabIndex = findIndex(newTabsState, (item) => item.siblingTabs[0]?.route === location.pathname);
        }
    });
});

const persistConfig: PersistConfig<typeof DEFAULT_STATE> = {
    key: 'tabBar',
    storage,
};

const persistTabBarReducer = persistReducer(persistConfig, tabBarReducer);

export { persistTabBarReducer as tabBarReducer };
